import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CalendarDays, Clock, Users, Loader2, ArrowRight } from "lucide-react";

import { formatEventLocal, formatEventUK, getCategory } from "@/lib/events";
import {
  listEventsForMember,
  listMyReservations,
  type MemberEvent,
} from "@/lib/events.functions";
import { ComplianceNotice } from "@/components/ComplianceNotice";

export const Route = createFileRoute("/my-events")({
  head: () => ({
    meta: [
      { title: "My Events — Recovery Trauma™" },
      {
        name: "description",
        content: "Live events you've reserved a place at on Recovery Trauma™.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: withMemberGate(MyEventsPage, "My Events"),
});

function MyEventsPage() {
  const fetchEvents = useServerFn(listEventsForMember);
  const fetchReservations = useServerFn(listMyReservations);

  const eventsQuery = useQuery({
    queryKey: ["events", "member"],
    queryFn: () => fetchEvents(),
  });
  const reservationsQuery = useQuery({
    queryKey: ["my-reservations"],
    queryFn: () => fetchReservations(),
  });

  const reservedIds = useMemo(
    () => new Set(reservationsQuery.data ?? []),
    [reservationsQuery.data],
  );

  const events = useMemo(() => {
    const all = eventsQuery.data ?? [];
    return all
      .filter((e) => reservedIds.has(e.id))
      .sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at));
  }, [eventsQuery.data, reservedIds]);

  const loading = eventsQuery.isLoading || reservationsQuery.isLoading;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:py-12">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <CalendarDays className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
          Your Reservations
        </div>
        <h1 className="font-display text-3xl font-semibold text-foreground md:text-5xl">
          My Events
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
          The Live Events you've reserved a place at. Your Zoom joining link
          and passcode are shown below and on your dashboard.
        </p>
      </header>

      <div className="mx-auto mb-8 max-w-2xl">
        <ComplianceNotice variant="events" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your events…
        </div>
      ) : events.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2">
          {events.map((e) => (
            <EventCard key={e.id} event={e} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-8 text-center text-sm text-muted-foreground">
          <p className="font-medium text-foreground">
            You haven't reserved a place at any events yet.
          </p>
          <p className="mt-2">
            Browse upcoming Live Events and reserve your spot.
          </p>
          <Link
            to="/events"
            className="mt-4 inline-flex items-center gap-1 rounded-full border border-gold/40 px-4 py-2 text-sm font-semibold text-[var(--gold-deep)] hover:bg-gold-light/40"
          >
            Browse Live Events <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}

function EventCard({ event }: { event: MemberEvent }) {
  const cat = getCategory(event.category);


  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gold/40 bg-card shadow-[var(--shadow-gold)]">
      {event.event_image_url ? (
        <div className={`relative aspect-[3/2] w-full overflow-hidden bg-gradient-to-br ${cat.hue}`}>
          <img
            src={event.event_image_url}
            alt={event.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <h2 className="font-display text-xl text-foreground md:text-2xl">
          {event.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">Host: {event.host}</p>

        <dl className="mt-4 space-y-2 text-sm">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              Your Local Time
            </dt>
            <dd className="text-foreground">{formatEventLocal(event.starts_at)}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Host Time (UK)
            </dt>
            <dd className="text-muted-foreground">{formatEventUK(event.starts_at)}</dd>
          </div>
        </dl>

        <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" /> {event.duration_minutes} min
        </div>

        <div className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-900">
          <Users className="h-3 w-3" />
          Your place is reserved
        </div>


        {event.zoom_url ? (
          <div className="mt-4 rounded-2xl border border-gold/35 bg-gold-light/25 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              Your joining details
            </p>
            <a
              href={event.zoom_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[var(--gold-deep)] px-5 text-sm font-semibold text-background transition hover:opacity-90"
            >
              Join on Zoom <ArrowRight className="h-4 w-4" />
            </a>
            {event.zoom_passcode ? (
              <p className="mt-2 text-sm text-foreground/80">
                Passcode: <span className="font-semibold">{event.zoom_passcode}</span>
              </p>
            ) : null}
            <p className="mt-1 break-all text-xs text-muted-foreground">{event.zoom_url}</p>
          </div>
        ) : null}

        <div className="mt-auto pt-5">
          <Link
            to="/events/$slug"
            params={{ slug: event.slug }}
            className="btn-gold text-sm"
          >
            View Event
          </Link>
        </div>
      </div>
    </article>
  );
}
